<template>
  <!-- „Für Sie heute“: deterministische Hinweise aus dem Benutzerkontext
       (überfällige Wiedervorlagen, Zustellprobleme, Neuerungen …). Jeder
       Hinweis ist ausblendbar; das merkt sich der Server für 7 Tage. -->
  <section v-if="insights.length > 0" :aria-label="$gt('Für Sie heute')" class="ai-insights">
    <h3 class="ai-insights__title">{{ $gt('Für Sie heute') }}</h3>
    <TransitionGroup class="ai-insights__list" name="ai-card" tag="ul">
      <li v-for="insight in insights" :key="insight.key" class="ai-card" :class="`is-${insight.severity}`">
        <span aria-hidden="true" class="ai-card__icon">
          <component :is="iconFor(insight)" :size="16" />
        </span>
        <div class="ai-card__body">
          <strong class="ai-card__title">{{ insight.title }}</strong>
          <p class="ai-card__text">{{ insight.text }}</p>
          <div class="ai-card__actions">
            <button v-if="insight.route" class="ai-link" type="button" @click="$emit('open', insight.route)">
              {{ $gt('Öffnen') }}
              <ArrowRight aria-hidden="true" :size="13" />
            </button>
            <button class="ai-text-btn" type="button" @click="$emit('ask', insight)">
              {{ $gt('Mehr dazu') }}
            </button>
          </div>
        </div>
        <button
          :aria-label="$gt('Hinweis ausblenden')"
          class="ai-icon-btn ai-icon-btn--dark ai-card__dismiss"
          :title="$gt('Für 7 Tage ausblenden')"
          type="button"
          @click="$emit('dismiss', insight.key)"
        >
          <X :size="14" />
        </button>
      </li>
    </TransitionGroup>
  </section>
</template>

<script setup lang="ts">
  import type { AssistantInsight, AssistantRoute } from '@/services/assistantApi'
  import {
    AlertTriangle,
    ArrowRight,
    BellRing,
    CalendarClock,
    Info,
    MailWarning,
    PartyPopper,
    Sparkles,
    X,
  } from 'lucide-vue-next'

  defineProps<{ insights: AssistantInsight[] }>()

  defineEmits<{
    (e: 'open', route: AssistantRoute): void
    (e: 'dismiss', key: string): void
    (e: 'ask', insight: AssistantInsight): void
  }>()

  const ICONS: Record<string, unknown> = {
    'followups-overdue': CalendarClock,
    'followups-today': CalendarClock,
    'unread-messages': BellRing,
    'delivery-problems': MailWarning,
    'first-login': PartyPopper,
    'whats-new': Sparkles,
    'aipages-low': AlertTriangle,
  }

  function iconFor (insight: AssistantInsight) {
    if (insight.key in ICONS) {
      return ICONS[insight.key]
    }
    return insight.severity === 'warn' ? AlertTriangle : Info
  }
</script>

<style scoped>
  .ai-insights {
    display: grid;
    gap: 8px;
  }

  .ai-insights__title {
    margin: 0;
    color: var(--ai-muted);
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
  }

  .ai-insights__list {
    display: grid;
    gap: 8px;
    margin: 0;
    padding: 0;
    list-style: none;
  }

  .ai-card {
    position: relative;
    display: grid;
    grid-template-columns: auto 1fr auto;
    gap: 10px;
    padding: 12px 8px 12px 12px;
    border: 1px solid var(--ai-line);
    border-left-width: 3px;
    border-left-color: var(--ai-blue);
    border-radius: 10px;
    background: var(--ai-surface);
  }

  .ai-card.is-warn {
    border-left-color: var(--ai-warn);
  }

  .ai-card.is-hint {
    border-left-color: var(--ai-blue);
  }

  .ai-card__icon {
    display: grid;
    place-items: center;
    width: 32px;
    height: 32px;
    border-radius: 8px;
    background: var(--ai-blue-soft);
    color: var(--ai-blue);
  }

  .ai-card.is-warn .ai-card__icon {
    background: var(--ai-warn-soft);
    color: var(--ai-warn);
  }

  .ai-card__body {
    min-width: 0;
  }

  .ai-card__title {
    display: block;
    color: var(--ai-ink);
    font-size: 13.5px;
    line-height: 1.3;
  }

  .ai-card__text {
    margin: 3px 0 0;
    color: var(--ai-muted);
    font-size: 13px;
    line-height: 1.45;
  }

  .ai-card__actions {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    align-items: center;
    margin-top: 8px;
  }

  .ai-card__dismiss {
    width: 28px;
    height: 28px;
    color: var(--ai-faint);
  }

  .ai-card-leave-active {
    transition: opacity 160ms ease, transform 200ms var(--ai-ease);
  }

  .ai-card-leave-to {
    opacity: 0;
    transform: translateX(12px);
  }
</style>
